import React, { FC, useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { RootState } from '../../../rootReducer';
import { prepareGridDataSelector } from '../Annotations/gridSelector';
import { ColumnProps } from '../../Shared/TableOptions/types';
import { Table } from 'antd';
import { AppDispatch } from '../../../store';
import { TABLE_STATUS } from '../../../shared/Helpers';
import { isGettingGridDataSelector } from './isGettingGridSelector';
import {
  setAllHiddenColumns,
  setAppliedFilters,
  setFindingChangedOnArrow,
  setSelectedAnnotationId,
  setStatusTable
} from '../Annotation/annotation.slice';
import TableOptions from '../../Shared/TableOptions/index';
import { adjustColumns } from '../../Shared/TableOptions/Helpers';
import { findPageWhereFindingIs, savedKeys, scrollToFindingRow } from './Helpers';
import { ParserContext } from '../contexts/ParserContext';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { isStringNullOrEmpty } from '../Annotation/AnnotationForm/Utils';
import { TablePaginationConfig } from 'antd/lib/table';
import UseAnnotationsHelpers from './UseAnnotationsHelpers';
import { setSaveFinding } from '../Canvas/canvas.slice';

const iconsConfig = { arrows: true, columnSelector: true };
const INITIAL_PAGE_SIZE = 20;

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

type AnnotationsProps = {
  fullScreenMode: boolean;
  onRowClicked: (sequencePath: string, imageName: string, id: string) => void;
};

export const Annotations: FC<RouteComponentProps<any> & Props & AnnotationsProps> = ({
  match,
  selectedAnnotationId,
  isGettingGrid,
  gridData,
  tableStatus,
  pathContext,
  selectedAnnotationForEdit,
  saveFinding,
  showCreateAnnotationModal,
  validationModalData,
  presentAnnotations,
  findingChangedOnArrow,
  fullScreenMode,
  filters,
  onRowClicked,
  onColumnsSelected,
  onSetStatusTable,
  onSelectAnnotationId,
  onSaveFinding,
  onFindingChangedOnArrow,
  saveFilters
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
  const [findingChanged, setFindingChanged] = useState(false);
  const { parser } = useContext(ParserContext);

  UseAnnotationsHelpers(
    gridData.dataSource,
    pathContext,
    saveFinding,
    showCreateAnnotationModal,
    validationModalData,
    presentAnnotations,
    selectedAnnotationId,
    onSaveFinding,
    onRowClicked,
    onFindingChangedOnArrow
  );

  useEffect(() => {
    if (selectedAnnotationId) {
      setFindingChanged(true);
    }
  }, [selectedAnnotationId]);

  useEffect(() => {
    if (!isStringNullOrEmpty(selectedAnnotationId)) {
      scrollToFindingRow(selectedAnnotationId);
    }
  }, [findingChanged, selectedAnnotationId]);

  useEffect(() => {
    if (findingChanged && findingChangedOnArrow) {
      const newPage = findPageWhereFindingIs(gridData.dataSource, currentPage, pageSize, selectedAnnotationId);
      if (newPage) {
        setCurrentPage(newPage!);
      }
      setFindingChanged(false);
      onFindingChangedOnArrow(false);
    }
  }, [currentPage, findingChanged, findingChangedOnArrow, gridData.dataSource, onFindingChangedOnArrow, pageSize, selectedAnnotationId]);

  useEffect(() => {
    const urlFindingId = parser.getAnnotationId(match.params);

    if (urlFindingId === selectedAnnotationId && selectedAnnotationForEdit) {
      return;
    }

    onSelectAnnotationId(urlFindingId ? urlFindingId : '');
  }, [match.params, onSelectAnnotationId, parser, selectedAnnotationForEdit, selectedAnnotationId]);

  const skippedColumns = pathContext === 'with-images' ? ['blade', 'bladeSide'] : [];
  const pagination: false | TablePaginationConfig =
    pathContext === 'with-images'
      ? false
      : {
          current: currentPage,
          pageSize: pageSize,
          showSizeChanger: true,
          onShowSizeChange: (current: number, size: number) => {
            setPageSize(size);
          },
          onChange(page, pageSize) {
            setCurrentPage(page);
          },
          size: 'small'
        };
  const minTableHeight = pathContext === 'with-images' ? 'calc((100vh - 129px)*0.35 - 90px)' : '14vh';

  if (fullScreenMode) {
    return null;
  }

  return (
    <>
      <TableOptions
        tableStatus={tableStatus}
        columns={adjustColumns(gridData).columns}
        onColumnsSelected={onColumnsSelected}
        onSetStatusTable={onSetStatusTable}
        iconsConfig={iconsConfig}
        savedKeys={savedKeys}
      />
      <Table
        style={{ minHeight: '270px', height: 'auto', display: tableStatus !== TABLE_STATUS.HIDE ? '' : 'none' }}
        className='annotation-canvas-table'
        dataSource={gridData.dataSource}
        columns={adjustColumns(gridData, skippedColumns, filters).allVisibleColumns}
        loading={isGettingGrid}
        onRow={(record: { sequencePath: string; key: string; imageName: string }) => ({
          onClick: () => {
            onRowClicked(record.sequencePath, record.imageName, record.key);
          }
        })}
        onChange={(pagination: TablePaginationConfig, filters: Record<string, any | null>) => {
          saveFilters(filters);
        }}
        pagination={pagination}
        rowClassName={(record: { key: string }) => {
          return record.key === selectedAnnotationId ? `selectable selectable-ac ${selectedAnnotationId}` : `selectable ${selectedAnnotationId}`;
        }}
        scroll={{
          y: tableStatus === TABLE_STATUS.EXPANDED ? 'calc(100vh - 270px)' : minTableHeight,
          x: gridData.columns.filter((x: ColumnProps) => x.isVisible).length * 100
        }}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  selectedAnnotationId: state.annotation.selectedAnnotationId,
  isGettingGrid: isGettingGridDataSelector(state),
  gridData: prepareGridDataSelector(state),
  tableStatus: state.annotation.tableStatus,
  pathContext: state.annotation.pathContext,
  selectedAnnotationForEdit: state.annotationForm.selectedAnnotationForEdit,
  canSaveAssessment: state.annotationForm.canSaveAssessment,
  currentPath: state.annotation.currentPath,
  saveFinding: state.canvas.saveFinding,
  showCreateAnnotationModal: state.annotationForm.showCreateAnnotationModal,
  validationModalData: state.annotationValidationModal.validationModalData,
  presentAnnotations: state.annotation.presentAnnotations,
  findingChangedOnArrow: state.annotation.findingChangedOnArrow,
  filters: state.annotation.appliedFilters
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  onSelectAnnotationId: (id: string) => dispatch(setSelectedAnnotationId(id)),
  onColumnsSelected: (toBeHidden: string[]) => dispatch(setAllHiddenColumns(toBeHidden)),
  onSetStatusTable: (expandHide: number) => dispatch(setStatusTable(expandHide)),
  onSaveFinding: (saveFinding: boolean) => dispatch(setSaveFinding(saveFinding)),
  onFindingChangedOnArrow: (value: boolean) => dispatch(setFindingChangedOnArrow(value)),
  saveFilters: (filters: Record<string, React.Key[] | null>) => dispatch(setAppliedFilters(filters))
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Annotations));
